import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Save } from 'lucide-react';
import { API_V1 } from '../../config/api';
import AccessEditor from '../../components/AccessMatrix/AccessEditor';
import styles from './GlobalAccessMatrix.module.css';

// HQ: the most access any user at a branch can have.
const API = `${API_V1}/smiify/hq/settings/ceilings`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

export default function GlobalAccessMatrix() {
    const navigate = useNavigate();
    const token = localStorage.getItem('smiify_token');
    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

    const [overview, setOverview] = useState(null);
    const [scope, setScope] = useState('ALL');
    const [detail, setDetail] = useState(null);
    const [draft, setDraft] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    const call = useCallback(async (url, opts = {}) => {
        const res = await fetch(url, { ...opts, headers });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) throw new Error(json.message || `Request failed (${res.status}).`);
        return json;
    }, [headers]);

    const loadOverview = useCallback(async () => setOverview((await call(API)).data), [call]);
    const loadScope = useCallback(async (s) => {
        const json = await call(`${API}/${s}`);
        setDetail(json.data);
        setDraft(json.data.policies);
    }, [call]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try { await Promise.all([loadOverview(), loadScope(scope)]); } catch (e) { setMsg({ type: 'error', text: e.message }); } finally { setLoading(false); }
        })();
    }, [scope, loadOverview, loadScope]);

    const dirty = detail && JSON.stringify(detail.policies) !== JSON.stringify(draft);
    const branch = overview?.branches?.find((b) => b.storeId === scope);

    const save = async () => {
        setSaving(true);
        setMsg(null);
        try {
            const json = await call(`${API}/${scope}`, { method: 'PUT', body: JSON.stringify({ policies: draft }) });
            setMsg({ type: 'ok', text: json.message });
            await Promise.all([loadOverview(), loadScope(scope)]);
        } catch (e) { setMsg({ type: 'error', text: e.message }); } finally { setSaving(false); }
    };

    const removeOwn = async () => {
        if (!window.confirm('Remove this branch\'s own limit? It will follow the limit for all branches.')) return;
        setSaving(true);
        try {
            const json = await call(`${API}/${scope}`, { method: 'DELETE' });
            setMsg({ type: 'ok', text: json.message });
            await Promise.all([loadOverview(), loadScope(scope)]);
        } catch (e) { setMsg({ type: 'error', text: e.message }); } finally { setSaving(false); }
    };

    const pick = (s) => {
        if (dirty && !window.confirm('You have unsaved changes. Leave them?')) return;
        setMsg(null);
        setScope(s);
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.headLeft}>
                    <button type="button" className={styles.iconBtn} onClick={() => navigate(-1)} aria-label="Back"><ArrowLeft size={15} /></button>
                    <div>
                        <h1>Branch access limits</h1>
                        <p>The most any user at a branch can do. Managers can give access only inside this limit.</p>
                    </div>
                </div>
                <div className={styles.headRight}>
                    {branch?.hasOwn && <button type="button" className={styles.btn} onClick={removeOwn} disabled={saving}>Use the limit for all branches</button>}
                    <button type="button" className={styles.btnPrimary} onClick={save} disabled={!dirty || saving}>
                        {saving ? <Loader2 size={13} className={styles.spin} /> : <Save size={13} />} Save limit
                    </button>
                </div>
            </header>

            <div className={styles.layout}>
                <aside className={styles.side}>
                    <button type="button" className={`${styles.scopeBtn} ${scope === 'ALL' ? styles.scopeOn : ''}`} onClick={() => pick('ALL')}>
                        <strong>All branches</strong>
                        <small>{overview?.all ? `Set ${fmt(overview.all.updatedAt)}` : 'No limit yet (everything allowed)'}</small>
                    </button>
                    <span className={styles.sideLabel}>Branches</span>
                    {(overview?.branches || []).map((b) => (
                        <button type="button" key={b.storeId} className={`${styles.scopeBtn} ${scope === b.storeId ? styles.scopeOn : ''}`} onClick={() => pick(b.storeId)}>
                            <strong>{b.name}</strong>
                            <small className={b.hasOwn ? styles.own : ''}>{b.hasOwn ? `Own limit, ${fmt(b.updatedAt)}` : 'Follows all branches'}</small>
                        </button>
                    ))}
                </aside>

                <main className={styles.main}>
                    {msg && (
                        <div className={`${styles.msg} ${msg.type === 'ok' ? styles.msgOk : styles.msgErr}`} role="status">
                            {msg.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {msg.text}
                        </div>
                    )}
                    {scope !== 'ALL' && detail && !detail.exists && (
                        <div className={styles.info}>This branch follows the limit for all branches (shown below). Change anything and save to give it its own limit.</div>
                    )}
                    {loading && !detail ? (
                        <div className={styles.loading}><Loader2 size={18} className={styles.spin} /> Loading...</div>
                    ) : overview && detail && (
                        <AccessEditor blueprint={overview.blueprint} categories={overview.categories} value={draft} onChange={setDraft} mode="CEILING" />
                    )}
                </main>
            </div>
        </div>
    );
}